
// client/src/components/media/SchedulePost.jsx
// UNIFIED VERSION - Same platform options and logic as MediaPost, plus scheduling
// UPDATED with TimeSelect and timezone support

import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import AppFooter from "../AppFooter";
import { FaCalendarCheck, FaSpinner, FaArrowLeft, FaEdit, FaMusic, FaFilm, FaGripVertical } from "react-icons/fa";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TimeSelect from '../TimeSelect';
import { localToUTC, utcToLocal, getNextSlotInTenantTZ } from '../../utils/timezoneUtils';
import PlatformSelector from '../PlatformSelector';
import MediaEditor from './MediaEditor';
import SubtitleEditor from './SubtitleEditor';
import MentionInput from './MentionInput';
import MediaTagOverlay, { FullSizeMediaModal } from './MediaTagOverlay';
import MusicPicker from './MusicPicker';
import ScheduleCalendar from './ScheduleCalendar';
import { api as apiClient } from '../../api/client';
import TokenExpiredModal from '../../modals/TokenExpiredModal';
import { checkPlatformAuth } from '../../utils/platformAuthCheck';

// Platforms whose terms of use treat identical reposts as spam. When the user
// turns on daily repeat with any of these selected we show a warning, and the
// publish workers auto-pause the series if a platform rejects the content
// (see services/seriesGuard.js).
const STRICT_REPEAT_PLATFORMS = ['instagram', 'twitter', 'threads', 'tiktok'];

// ---- helpers (same as MediaPost) ---------------------------------------------------------------
const isImageUrl = (u = "") => {
  const clean = String(u).split("?")[0];
  const ext = clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  return ["jpg", "jpeg", "png", "gif", "webp", "heic", "avif"].includes(ext);
};


const detectKind = (urls = []) => {
  const first = (urls[0] || "").toLowerCase().split("?")[0];
  if (/\.(mp4|m4v)$/.test(first)) return "video";
  if (/\.(mov|qt)$/.test(first)) return "mov";
  if (/\.(jpg|jpeg|png|gif|webp|heic|avif)$/.test(first)) return "image";
  return "";
};

const isLocalPublic = (u, base) => {
  try {
    const root = String(base).replace(/\/+$/, "");
    const abs = /^https?:\/\//i.test(String(u)) ? String(u) : `${root}/${String(u).replace(/^\/+/, "")}`;
    const url = new URL(abs);
    return url.pathname.startsWith("/public/");
  } catch {
    return false;
  }
};

// Wraps any media tile so the user can drag it to reorder. The grip button is
// the only drag handle, leaving the rest of the tile (MediaTagOverlay, Edit
// button, native video controls) free for clicks/taps.
function SortableMediaItem({ id, children, className, style, handleVariant = "light" }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const wrapperStyle = {
    position: "relative",
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 5 : "auto",
  };
  // Touch-friendly grip handle. Bigger hit target on the small (non-cover) tiles
  // because users were finding it hard to grab on phones — the tile itself
  // captures taps to open the full-size tag viewer, so the grip needs to be
  // clearly distinct.
  const handleSize = handleVariant === "small"
    ? { padding: "8px 10px", fontSize: 16, top: 4, left: 4 }
    : { padding: "10px 12px", fontSize: 18, top: 6, left: 6 };
  return (
    <div ref={setNodeRef} className={className} style={wrapperStyle} {...attributes}>
      <button
        type="button"
        {...listeners}
        title="Drag to reorder"
        aria-label="Drag to reorder media"
        style={{
          position: "absolute",
          background: "rgba(0,0,0,0.7)",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "grab",
          touchAction: "none",
          display: "flex",
          alignItems: "center",
          zIndex: 4,
          ...handleSize,
        }}
      >
        <FaGripVertical />
      </button>
      {children}
    </div>
  );
}

export default function SchedulePost() {
  const { tenant, eventId, itemId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    window.location.origin ||
    "http://localhost:5000";

  // Normalize to absolute, publicly reachable URL (same as MediaPost)
  const toPublic = (u) => {
    if (!u) return u;
    const root = String(baseUrl).replace(/\/+$/, "");
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/public/")) return `${root}${u}`;
    if (u.startsWith("/tenants/")) return `${root}/public${u}`;
    const combined = `${root}/${String(u).replace(/^\/+/, "")}`;
    return encodeURI(combined);
  };


  // ---------------- incoming defaults (same as MediaPost) ----------------
  const defaultText =
    state?.content || state?.caption || state?.title || state?.description || "";

  const fromStateImages =
    (Array.isArray(state?.images) && state.images) ||
    (Array.isArray(state?.mediaUrls) && state.mediaUrls) ||
    (Array.isArray(state?.media) && state.media) ||
    (Array.isArray(state?.selectedMedia?.images) && state.selectedMedia.images) ||
    (Array.isArray(state?.media?.images) && state.media.images) ||
    [];

  const fromStateVideos =
    (Array.isArray(state?.videos) && state.videos) ||
    (Array.isArray(state?.selectedMedia?.videos) && state.selectedMedia.videos) ||
    (Array.isArray(state?.media?.videos) && state.media.videos) ||
    [];

  const unique = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

  const imagesRaw = useMemo(() => unique(fromStateImages), [fromStateImages]);
  const videosRaw = useMemo(() => unique(fromStateVideos), [fromStateVideos]);

  const imagesAbsAll = useMemo(() => imagesRaw.map(toPublic), [imagesRaw]);

  // Images + videos are stateful so the user can drag-reorder them.
  // We seed once from the incoming route state. The composer is a single-shot
  // navigation target, so we don't need to reseed on prop change — and doing so
  // would clobber the user's in-progress reorder.
  const [imagesAbs, setImagesAbs] = useState(() => imagesAbsAll.filter(isImageUrl));
  const [videosAbs, setVideosAbs] = useState(() => videosRaw.map(toPublic));

  // ---------------- local state (same as MediaPost + scheduling) ----------------
  const [content, setContent] = useState(defaultText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [expiredModal, setExpiredModal] = useState({ open: false, platforms: [] });

  // Image editor state
  const [editingImageIdx, setEditingImageIdx] = useState(null);
  const [editedImages, setEditedImages] = useState([]);

  // User tags on media (per image index): { [imageIdx]: [{ username, x, y }] }
  const [userTags, setUserTags] = useState({});

  // Index of the image currently shown full-size for tagging. null = closed.
  const [fullSizeImgIdx, setFullSizeImgIdx] = useState(null);

  // Video subtitle editor state
  const [editingVideoIdx, setEditingVideoIdx] = useState(null);
  const [videoSubtitles, setVideoSubtitles] = useState([]); // VTT URLs per video index

  // Effective images: use edited replacements where available
  const effectiveImages = useMemo(() =>
    imagesAbs.map((url, i) => editedImages[i] || url),
    [imagesAbs, editedImages]
  );

  // Image-to-video conversion (see MediaPost for the same affordance).
  const [convertingToVideo, setConvertingToVideo] = useState(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
  const [imgToVideoSeconds, setImgToVideoSeconds] = useState(4);
  const handleConvertToVideo = async () => {
    setConvertingToVideo(true);
    setError("");
    try {
      const { data } = await axios.post(
        `/api/media/image-to-video`,
        { imageUrls: effectiveImages, secondsPerImage: imgToVideoSeconds },
        { withCredentials: true, timeout: 180000 }
      );
      if (!data?.ok || !data?.url) throw new Error(data?.error || 'Conversion failed');
      setGeneratedVideoUrl(data.url);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Conversion failed');
    } finally {
      setConvertingToVideo(false);
    }
  };

  const blueskyImages = useMemo(
    () => effectiveImages.filter(isImageUrl).slice(0, 4),
    [effectiveImages]
  );

  // SAME platform toggles as MediaPost (for own tenant). When reopening an
  // existing scheduled post (state.platforms), pre-check whichever platforms
  // it was originally scheduled for instead of starting with none selected.
  const [platforms, setPlatforms] = useState(() => {
    const base = {
      instagram: false,
      facebook: false,
      twitter: false,
      linkedin: false,
      bluesky: false,
      threads: false,
      youtube: false,
      tiktok: false,
      reddit: false,
    };
    if (Array.isArray(state?.platforms)) {
      state.platforms.forEach((p) => {
        const key = String(p).toLowerCase();
        if (key in base) base[key] = true;
      });
    }
    return base;
  });

  // Music-on-posts state (mirrors MediaPost)
  const musicDraftRef = useRef(`draft_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
  const [musicPickerOpen, setMusicPickerOpen] = useState(false);
  const [musicAttachment, setMusicAttachment] = useState(null);
  const handleMusicAttached = ({ attachmentId, track, tiktokUseNativeSound }) => {
    setMusicAttachment({ id: attachmentId, track, tiktokUseNativeSound, status: tiktokUseNativeSound ? 'ready' : 'pending' });
  };
  const removeMusic = async () => {
    if (!musicAttachment?.id) { setMusicAttachment(null); return; }
    try { await apiClient.delete(`/music/attachment/${musicAttachment.id}`); } catch (err) { console.warn('removeMusic failed', err); }
    setMusicAttachment(null);
  };

  // Multi-account posting: collaborator accounts and their platform selections
  const [collabAccounts, setCollabAccounts] = useState([]); // from API
  const [collabSelections, setCollabSelections] = useState({}); // { [tenantId]: { [platform]: bool } }

  // Instagram/Facebook Reel options — see MediaPost for full rationale.
  const [igPostKind, setIgPostKind] = useState('auto');
  const [igShareReelToFeed, setIgShareReelToFeed] = useState(true);

  // Reddit-specific state
  const [redditSubreddits, setRedditSubreddits] = useState([]);
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [redditTitle, setRedditTitle] = useState('');

  // TikTok Direct Post state
  const [tiktokMode, setTiktokMode] = useState('upload');
  const [tiktokTitle, setTiktokTitle] = useState(''); // Point 2a: Title field
  const [tiktokPrivacy, setTiktokPrivacy] = useState(''); // No default per TikTok guidelines
  const [tiktokDisableDuet, setTiktokDisableDuet] = useState(true); // Unchecked by default per guidelines
  const [tiktokDisableStitch, setTiktokDisableStitch] = useState(true);
  const [tiktokDisableComment, setTiktokDisableComment] = useState(true);
  const [tiktokIsDirect, setTiktokIsDirect] = useState(false);
  const [tiktokCreator, setTiktokCreator] = useState(null);
  const [tiktokCommercialToggle, setTiktokCommercialToggle] = useState(false);
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false);
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false);

  // Fetch Reddit subreddits when Reddit is toggled on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    if (platforms.reddit && redditSubreddits.length === 0) {
      const tenantId = Number(tenant) || 1;
      fetch(`${baseUrl}/api/integrations/reddit/subreddits?tenantId=${tenantId}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.items) setRedditSubreddits(data.items);
        })
        .catch(err => console.error('Failed to fetch subreddits:', err));
    }
  }, [platforms.reddit]);

  // Check TikTok status and fetch creator info when TikTok is toggled on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    if (platforms.tiktok) {
      const tenantId = Number(tenant) || 1;
      fetch(`${baseUrl}/api/integrations/tiktok/status?tenantId=${tenantId}`, { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          const hasDirect = data.connected && data.isDirect;
          setTiktokIsDirect(hasDirect);
          if (!hasDirect) setTiktokMode('upload');
          if (hasDirect) {
            fetch(`${baseUrl}/api/integrations/tiktok/creator-info?tenantId=${tenantId}`, { credentials: 'include' })
              .then(r => r.json())
              .then(ci => {
                if (ci.success && ci.creator) setTiktokCreator(ci.creator);
              })
              .catch(err => console.error('Failed to fetch TikTok creator info:', err));
          }
        })
        .catch(err => console.error('Failed to check TikTok status:', err));
    }
  }, [platforms.tiktok]);

  // NEW: Tenant timezone
  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');
  // Loading flag — prevents the form from showing browser-local placeholder times
  // that would then visibly jump when the tenant timezone arrives.
  const [timezoneReady, setTimezoneReady] = useState(false);

  // Scheduling state — intentionally empty until timezone is confirmed so there
  // is no brief flash of the wrong (browser-local) time.
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');

  // NEW: Repeat-post (multi-day) state. Default is a single post; turning on
  // repeat reveals the calendar picker. repeatSlots = [{ date, time }] (one per
  // calendar day, enforced by ScheduleCalendar and again on the server).
  const [repeatMode, setRepeatMode] = useState(false);
  const [repeatSlots, setRepeatSlots] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const selected = useMemo(
    () => Object.entries(platforms).filter(([, v]) => v).map(([k]) => k),
    [platforms]
  );

  // Strict-ToS platforms currently selected — drives the duplicate-content warning.
  const repeatRiskyPlatforms = useMemo(
    () => selected.filter((p) => STRICT_REPEAT_PLATFORMS.includes(p)),
    [selected]
  );

  // Fetch collaborator accounts when posting from an event
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        const res = await axios.get(`/api/collab/events/${eventId}/postable-accounts`, { withCredentials: true });
        const accounts = res?.data?.accounts || [];
        // Exclude own tenant (already handled by main platform selector)
        const others = accounts.filter(a => a.tenantId !== Number(tenant));
        setCollabAccounts(others);
      } catch (err) {
        console.error('Failed to fetch postable accounts:', err);
      }
    })();
  }, [eventId, tenant]);

  // Fetch tenant timezone on mount — sets date/time only AFTER the timezone is
  // known so there is no jump from browser-local time to tenant time.
  useEffect(() => {
    const fetchTenantTimezone = async () => {
      let tz = 'America/Chicago';
      try {
        const response = await axios.get(
          `${baseUrl}/api/tenants/${tenant}/timezone`,
          { withCredentials: true }
        );
        if (response.data.timezone) tz = response.data.timezone;
      } catch (err) {
        console.error('Failed to fetch tenant timezone:', err);
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      setTenantTimezone(tz);
      // If navigating from an existing scheduled post, pre-fill its time (UTC → tenant TZ).
      // Otherwise default to "now + 15 min" in tenant TZ.
      const existingUtc = state?.scheduledOn;
      if (existingUtc) {
        const { date, time } = utcToLocal(existingUtc, tz);
        setScheduleDate(date);
        setScheduleTime(time);
      } else {
        const nextSlot = getNextSlotInTenantTZ(tz, 15);
        setScheduleDate(nextSlot.date);
        setScheduleTime(nextSlot.time);
      }
      setTimezoneReady(true);
    };

    if (tenant) {
      fetchTenantTimezone();
    }
  }, [tenant, baseUrl]);

  // Always land at the top of the form. Without this, navigating here from a
  // scrolled-down list (e.g. clicking a card in ScheduledPostsList) leaves the
  // page wherever the browser's scroll position happened to be, since SPA
  // route changes don't reset scroll on their own.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ---------------- actions (same as MediaPost) ----------------
  const openBlueskyConnect = () => {
    const url = `${baseUrl}/api/integrations/bluesky/login?tenantId=${Number(tenant || 1)}`;
    window.open(url, "bsky-connect", "width=520,height=520,menubar=no,toolbar=no");
  };

  const ensureIgReady = async (tenantId) => {
    const safeFetch = async (url) => {
      try {
        const r = await fetch(url, { credentials: "include" });
        return r.ok ? r.json() : null;
      } catch {
        return null;
      }
    };
    // Check BOTH peek endpoints. FB peek lists Facebook Pages (which may or may
    // not have a linked IG account); IG _peek also includes Instagram Business
    // Login connections that don't go through a Facebook Page. Merge both so
    // either path counts as eligible.
    const [fbPeek, igPeek] = await Promise.all([
      safeFetch(`${baseUrl}/api/integrations/facebook/peek?tenantId=${tenantId}`),
      safeFetch(`${baseUrl}/api/integrations/instagram/_peek?tenantId=${tenantId}`),
    ]);
    const pages = [
      ...(Array.isArray(fbPeek?.pages) ? fbPeek.pages : []),
      ...(Array.isArray(igPeek?.pages) ? igPeek.pages : []),
    ];
    const eligible = pages.some((p) => p?.igUserId && (p?.hasPageToken || p?.pageToken));
    if (!eligible) {
      throw new Error(
        "No connected Facebook/IG account for tenant. Go to Integrations → Facebook/Instagram and connect a Page linked to your IG."
      );
    }
  };

  // Handle editor save: MediaEditor uploads internally and returns the new URL
  const handleEditorSaved = (newUrl) => {
    const publicUrl = toPublic(newUrl);
    setEditedImages(prev => {
      const copy = [...prev];
      copy[editingImageIdx] = publicUrl;
      return copy;
    });
    setEditingImageIdx(null);
  };

  // Handle subtitle editor save: stores the VTT URL for the video
  const handleSubtitleSaved = (vttUrl) => {
    const publicUrl = vttUrl ? toPublic(vttUrl) : null;
    setVideoSubtitles(prev => {
      const copy = [...prev];
      copy[editingVideoIdx] = publicUrl;
      return copy;
    });
    setEditingVideoIdx(null);
  };

  // ---------------- Drag-and-drop reordering ----------------
  // Mouse must move 5px before drag starts so clicks on Edit / Tag overlays
  // still register. Touch requires a 200ms hold to avoid hijacking taps.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // userTags is an index-keyed object; treat it like a sparse array so a
  // permutation moves a tag set with its image.
  const permuteIndexMap = (map, from, to) => {
    if (from === to) return map;
    const asArr = [];
    Object.keys(map).forEach((k) => { asArr[Number(k)] = map[k]; });
    const moved = arrayMove(asArr, from, to);
    const out = {};
    moved.forEach((v, i) => { if (v !== undefined) out[i] = v; });
    return out;
  };

  const handleImageDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = imagesAbs.indexOf(active.id);
    const to = imagesAbs.indexOf(over.id);
    if (from === -1 || to === -1) return;
    setImagesAbs((prev) => arrayMove(prev, from, to));
    setEditedImages((prev) => arrayMove(prev, from, to));
    setUserTags((prev) => permuteIndexMap(prev, from, to));
  };

  const handleVideoDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = videosAbs.indexOf(active.id);
    const to = videosAbs.indexOf(over.id);
    if (from === -1 || to === -1) return;
    setVideosAbs((prev) => arrayMove(prev, from, to));
    setVideoSubtitles((prev) => arrayMove(prev, from, to));
  };

  // ---------------- MAIN SUBMIT (schedule instead of immediate post) ----------------
  const submit = async () => {
    try {
      setBusy(true);
      setError("");
      setSuccessMessage("");

      // Validation: at least one platform selected
      if (!selected.length) {
        throw new Error("Select at least one platform.");
      }

      // Define tenantId from params
      const tenantId = Number(tenant) || 1;

      // Check platform authorization before scheduling
      const { missing, expired } = await checkPlatformAuth(baseUrl, tenantId, selected);
      if (missing.length) {
        const names = missing.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
        toast.warn(`Please connect to ${names} before scheduling.`);
        navigate(`/${tenant}/integrations`);
        setBusy(false);
        return;
      }
      if (expired.length) {
        const names = expired.map(p => p.charAt(0).toUpperCase() + p.slice(1));
        setExpiredModal({ open: true, platforms: names });
        setBusy(false);
        return;
      }

      // Prepare media URLs (same as MediaPost). If the user converted images
      // to a video, schedule the rendered slideshow instead of the stills.
      const mediaUrlsForOthers = generatedVideoUrl
        ? unique([generatedVideoUrl, ...videosAbs])
        : unique([...videosAbs, ...effectiveImages]);
      const detected = detectKind(mediaUrlsForOthers);
      // Same kind-mapping as MediaPost — see comment there for full rationale.
      const isVideoDetected = detected === "video" || detected === "mov";
      let inferredKind;
      if (detected === "image") {
        inferredKind = "image";
      } else if (isVideoDetected) {
        const choice = igPostKind === 'auto' ? 'reel' : igPostKind;
        inferredKind = choice === 'reel' ? 'reel' : '';
      } else {
        inferredKind = "";
      }

      // Instagram validations (same as MediaPost)
      if (platforms.instagram) {
        await ensureIgReady(tenantId);

        if (detected === "mov") {
          const first = mediaUrlsForOthers[0] || "";
          if (!isLocalPublic(first, baseUrl)) {
            throw new Error(
              "Instagram only accepts MP4 (H.264 + AAC). Convert your MOV to MP4 or upload it to this app (under /public) so we can auto-convert."
            );
          }
        }

        if (!mediaUrlsForOthers.length && !content.trim()) {
          throw new Error("Instagram requires at least one image/video or a caption.");
        }
      }

      // TikTok Direct Post validation
      if (platforms.tiktok && tiktokMode === 'direct') {
        // Point 1b: Block posting when creator can't post more
        if (tiktokCreator?.cannotPostReason) {
          throw new Error("You've reached TikTok's posting limit. Please try again later.");
        }
        if (!tiktokPrivacy) throw new Error("Please select a privacy level for your TikTok post.");
        if (tiktokCommercialToggle && !tiktokBrandOrganic && !tiktokBrandContent) {
          throw new Error("You need to indicate if your content promotes yourself, a third party, or both.");
        }
        // Point 1c: Check video duration against creator max
        if (videosAbs.length > 0 && tiktokCreator?.maxVideoPostDurationSec) {
          const videoEl = document.querySelector('video');
          if (videoEl && videoEl.duration > tiktokCreator.maxVideoPostDurationSec) {
            throw new Error(`Video duration (${Math.round(videoEl.duration)}s) exceeds TikTok's maximum of ${tiktokCreator.maxVideoPostDurationSec}s for your account.`);
          }
        }
      }

      // Reddit validation
      if (platforms.reddit) {
        if (!redditSubreddit) throw new Error("Please select a subreddit for your Reddit post.");
        if (!redditTitle.trim()) throw new Error("Reddit posts require a title.");
      }

      // Prepare Instagram carousel if multiple images
      const igCarousel = effectiveImages.length > 1
        ? effectiveImages.slice(0, 10).map((u) => ({ imageUrl: u }))
        : undefined;

      // Helper: tenant-local date+time -> UTC MySQL datetime string (with seconds).
      const toUtcMySql = (dateStr, timeStr) => {
        const raw = localToUTC(dateStr, timeStr, tenantTimezone);
        if (!raw) return null;
        return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(raw) ? `${raw}:00` : raw;
      };

      // Build accountSelections for multi-account posting
      const accountSelections = [
        // Own tenant's selected platforms
        { tenantId, platforms: selected },
        // Collaborator tenant selections
        ...Object.entries(collabSelections)
          .map(([tid, platMap]) => ({
            tenantId: Number(tid),
            platforms: Object.entries(platMap).filter(([, v]) => v).map(([k]) => k),
          }))
          .filter(sel => sel.platforms.length > 0),
      ];

      // Fields shared by single and repeat posts (everything except the
      // schedule time itself, which differs per mode).
      const basePayload = {
        tenantId,
        eventId: eventId ? Number(eventId) : null,
        eventItemId: itemId ? Number(itemId) : null,
        platforms: selected,
        accountSelections,
        content,
        caption: content,
        mediaUrls: mediaUrlsForOthers,
        kind: inferredKind,
        shareToFeed: inferredKind === 'reel' ? igShareReelToFeed : true,
        thumbOffsetSec: 0,
        carouselItems: igCarousel,

        // Music-on-posts: tell the schedule route to re-point the t_post_music
        // row at the new social_post; socialPoster.js handles the URL swap at
        // publish time. The mix worker runs in the background until then.
        musicDraftRef: musicAttachment?.id ? musicDraftRef.current : undefined,

        timezone: tenantTimezone,

        // User tags on media (per-image array of { username, x, y })
        userTags: Object.keys(userTags).length > 0 ? userTags : undefined,

        // Reddit-specific fields (stored in provider_payload for the worker)
        ...(platforms.reddit && {
          redditSubreddit,
          redditTitle,
        }),

        // TikTok-specific fields (stored in provider_payload for the worker)
        ...(platforms.tiktok && {
          tiktokMode,
          tiktokTitle, // Point 2a: Title field
          ...(tiktokMode === 'direct' && {
            tiktokPrivacy,
            tiktokDisableDuet,
            tiktokDisableStitch,
            tiktokDisableComment,
            ...(tiktokCommercialToggle && {
              tiktokBrandOrganic,
              tiktokBrandContent,
            }),
          }),
        }),
      };

      // Warn (don't silently block) if this exact text was already posted or
      // attempted on X. X's duplicate/spam-content filter rejects reused text
      // with an opaque rejection that previously only showed up as a "FAILED"
      // badge, with no reason, well after the fact. Applies to repeat/batch
      // schedules too — those post the SAME text across multiple days, which
      // is exactly the pattern X's filter rejects, so it's checked here once
      // up front for the whole series rather than per-day.
      if (platforms.twitter && content.trim()) {
        try {
          const { data: reuseCheck } = await axios.get(
            `/api/schedule/check-reuse/${tenantId}`,
            { params: { content, platform: 'twitter' }, withCredentials: true, timeout: 10000 }
          );
          if (reuseCheck?.reused) {
            const when = reuseCheck.when ? new Date(reuseCheck.when).toLocaleDateString() : 'recently';
            const verb = reuseCheck.priorStatus === 'posted' ? 'posted' : 'attempted';
            const extra = repeatMode
              ? ' This will also repeat the same text across every day in this series, compounding the risk of rejection.'
              : '';
            const proceed = window.confirm(
              `This exact text was already ${verb} to X (Twitter) on ${when}. ` +
              `X often rejects repeated/duplicate text as spam, which would fail this post too.${extra} ` +
              `Schedule it anyway?`
            );
            if (!proceed) {
              setBusy(false);
              return;
            }
          }
        } catch {
          // Best-effort — never block scheduling because the check itself failed.
        }
      }

      const now = new Date();
      let response;

      if (repeatMode) {
        // ---- Repeat (multi-day) schedule ----
        if (!repeatSlots.length) {
          setError("Pick at least one day to repeat this post, or turn Repeat off.");
          setBusy(false);
          return;
        }
        // Convert each selected day to a UTC slot, dropping any that landed in
        // the past (the server re-validates and also dedupes to one per day).
        const slots = [];
        for (const s of repeatSlots) {
          const utc = toUtcMySql(s.date, s.time);
          if (!utc) continue;
          if (new Date(utc.replace(' ', 'T') + 'Z') <= now) continue;
          slots.push({ scheduledAtLocal: utc });
        }
        if (!slots.length) {
          setError("All selected times are in the past. Adjust the times and try again.");
          setBusy(false);
          return;
        }

        response = await axios.post(
          `/api/schedule/schedule-batch`,
          { ...basePayload, slots },
          { withCredentials: true, timeout: 60000 }
        );

        if (response.data.success) {
          setSuccessMessage(
            `Repeat post scheduled across ${response.data.count} day(s)! ` +
            `Each day will be published to ${selected.join(', ')}.`
          );
          setTimeout(() => navigate(-1), 2000);
        } else {
          throw new Error(response.data.error || "Failed to schedule repeat post");
        }
      } else {
        // ---- Single post (unchanged behavior) ----
        const scheduledUtcMySql = toUtcMySql(scheduleDate, scheduleTime);
        const scheduledUtcIso = new Date(scheduledUtcMySql.replace(' ', 'T') + 'Z').toISOString();
        const scheduledDateTime = new Date(scheduledUtcIso);

        if (scheduledDateTime <= now) {
          setError("Scheduled time must be in the future. Please select a later date/time.");
          setBusy(false);
          return;
        }

        response = await axios.post(
          `/api/schedule/schedule`,
          {
            ...basePayload,
            // Backend route prefers scheduledAtLocal (MySQL-friendly)
            scheduledAtLocal: scheduledUtcMySql,
            scheduledAtIso: scheduledUtcIso,
            scheduledAt: scheduledUtcIso,
          },
          { withCredentials: true, timeout: 30000 }
        );

        if (response.data.success) {
          const scheduledDisplay = scheduledDateTime.toLocaleString('en-US', {
            timeZone: tenantTimezone,
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
          setSuccessMessage(
            `Post scheduled successfully for ${scheduledDisplay}! ` +
            `It will be published to ${selected.join(', ')}.`
          );
          setTimeout(() => navigate(-1), 2000);
        } else {
          throw new Error(response.data.error || "Failed to schedule post");
        }
      }

    } catch (e) {
      console.error("Error scheduling post:", e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e.message ||
        "Failed to schedule post";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  // Disable submit when TikTok commercial toggle is on but no sub-option selected
  const tiktokCommercialIncomplete = platforms.tiktok && tiktokMode === 'direct' &&
    tiktokCommercialToggle && !tiktokBrandOrganic && !tiktokBrandContent;
  // Disable submit when TikTok creator can't post (Point 1b)
  const tiktokCannotPost = platforms.tiktok && tiktokMode === 'direct' && tiktokCreator?.cannotPostReason;

  const canSubmit =
    selected.length > 0 &&
    (content.trim().length > 0 || effectiveImages.length > 0 || videosAbs.length > 0) &&
    // In repeat mode at least one day must be picked.
    (!repeatMode || repeatSlots.length > 0) &&
    !tiktokCommercialIncomplete &&
    !tiktokCannotPost;

  // ---------------- render (same as MediaPost + date/time picker) ----------------
  return (
    <div className="container py-3 schedule-post-page">
      <h2 className="hdr hdr--primary hdr--lg">Schedule Post</h2>

      {/* Desktop inline action bar — placed directly after the header so it
          pins to the top on desktop, matching the other form pages (EventAdd,
          MemberAdd, UserAdd). Hidden on mobile, where AppFooter takes over. */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)} disabled={busy}>
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          onClick={() => setMusicPickerOpen(true)}
          title="Add music to this post (requires a video)"
        >
          <FaMusic style={{ color: '#fff' }} /> {musicAttachment ? 'Edit music' : 'Add music'}
        </button>
        <button
          type="button"
          className="btn-primary-action"
          onClick={submit}
          disabled={busy || !canSubmit}
          title={
            tiktokCommercialIncomplete
              ? "You need to indicate if your content promotes yourself, a third party, or both."
              : tiktokCannotPost
              ? "You've reached TikTok's posting limit. Please try again later."
              : undefined
          }
        >
          {busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />}{' '}
          {busy ? "Scheduling…" : repeatMode ? "Schedule Posts" : "Schedule Post"}
        </button>
      </div>

      {/* Integrations - Dropdown Selector (same as MediaPost) */}
      <div className="mb-3">
        <div className="form-label font-semibold mb-2">Select Platforms</div>
        <PlatformSelector
          platforms={platforms}
          setPlatforms={setPlatforms}
          onConnect={(platformKey) => {
            if (platformKey === 'bluesky') {
              openBlueskyConnect();
            }
          }}
          showConnectFor={['bluesky']}
        />
      </div>

      {/* Collaborator accounts (multi-account posting) */}
      {collabAccounts.length > 0 && (
        <div className="mb-3 p-3 rounded border">
          <div className="form-label font-semibold mb-2">Collaborator Accounts</div>
          <p className="text-sm text-neutral-500 mb-2">
            Also publish this post through collaborator platform accounts:
          </p>
          {collabAccounts.map((acct) => (
            <div key={acct.tenantId} className="mb-2 p-2 rounded bg-neutral-50 dark:bg-neutral-800">
              <div className="text-sm font-medium mb-1">{acct.tenantName}</div>
              <div className="flex flex-wrap gap-2">
                {acct.platforms.map((plat) => {
                  const checked = !!collabSelections[acct.tenantId]?.[plat];
                  return (
                    <label key={plat} className="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setCollabSelections((prev) => ({
                            ...prev,
                            [acct.tenantId]: {
                              ...prev[acct.tenantId],
                              [plat]: e.target.checked,
                            },
                          }));
                        }}
                      />
                      {plat.charAt(0).toUpperCase() + plat.slice(1)}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content (with @mention autocomplete) */}
      <div className="mb-3">
        <label className="form-label">Post Content</label>
        <MentionInput
          value={content}
          onChange={setContent}
          rows={5}
          placeholder="Write your post... Type @ to mention a platform handle"
        />
        <div className="form-text">Type @ to mention platform handles (e.g. @nike, @caborojo)</div>
      </div>

      {/* Instagram / Facebook Reel options — only shown when at least one of
          IG/FB is selected AND a video is attached. */}
      {(platforms.instagram || platforms.facebook) && videosAbs.length > 0 && (
        <div className="mb-3 p-3 rounded" style={{ background: '#fdf2ff', border: '1px solid #C13584', color: '#212529' }}>
          <div className="font-semibold mb-2" style={{ color: '#C13584' }}>Instagram / Facebook Video Settings</div>
          <div className="mb-2">
            <label className="form-label small mb-1">Post Type</label>
            <select
              className="form-select"
              value={igPostKind}
              onChange={(e) => setIgPostKind(e.target.value)}
            >
              <option value="auto">Auto (Reel for video)</option>
              <option value="reel">Reel</option>
              <option value="feed">Feed Post</option>
            </select>
            <div className="form-text">
              Instagram always publishes video as a Reel. This toggle controls
              the Facebook Page placement: <em>Reel</em> uses the Reels tab,
              <em> Feed Post</em> uses regular Page video.
            </div>
          </div>
          {(igPostKind === 'auto' || igPostKind === 'reel') && (
            <div className="form-check form-switch">
              <input
                id="schedule-ig-share-reel-to-feed"
                className="form-check-input"
                type="checkbox"
                checked={igShareReelToFeed}
                onChange={(e) => setIgShareReelToFeed(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="schedule-ig-share-reel-to-feed">
                Share Reel to Instagram feed (in addition to Reels tab)
              </label>
            </div>
          )}
        </div>
      )}

      {/* Reddit-specific fields */}
      {platforms.reddit && (
        <div className="mb-3 p-3 rounded" style={{ background: '#fff4f0', border: '1px solid #FF4500' }}>
          <div className="font-semibold mb-2" style={{ color: '#FF4500' }}>Reddit Post Settings</div>
          <div className="mb-2">
            <label className="form-label small mb-1">Subreddit *</label>
            <select
              className="form-select"
              value={redditSubreddit}
              onChange={(e) => setRedditSubreddit(e.target.value)}
            >
              <option value="">Select a subreddit...</option>
              {redditSubreddits.map((sr) => (
                <option key={sr.name} value={sr.name}>r/{sr.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Post Title *</label>
            <input
              type="text"
              className="form-control"
              value={redditTitle}
              onChange={(e) => setRedditTitle(e.target.value)}
              placeholder="Enter a title for your Reddit post"
              maxLength={300}
            />
            <div className="form-text">{redditTitle.length}/300 characters</div>
          </div>
        </div>
      )}

      {/* TikTok-specific fields */}
      {platforms.tiktok && (
        <div className="mb-3 p-3 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)' }}>
          <div className="font-semibold mb-2">TikTok Post Settings</div>

          {/* Post Mode */}
          <div className="mb-2">
            <label className="form-label small mb-1">Post Mode</label>
            <select
              className="form-select"
              value={tiktokMode}
              onChange={(e) => setTiktokMode(e.target.value)}
              disabled={!tiktokIsDirect}
            >
              <option value="upload">Upload to Inbox (draft for review)</option>
              <option value="direct" disabled={!tiktokIsDirect}>Direct Post (publish immediately)</option>
            </select>
            {!tiktokIsDirect && (
              <div className="form-text">Direct Post requires TikTok app approval. Currently using Upload to Inbox.</div>
            )}
          </div>

          {/* Direct Post settings - only show when direct mode is selected */}
          {tiktokMode === 'direct' && (
            <>
              {/* === POINT 1: Creator Info === */}
              {/* Point 1a: Creator nickname */}
              {tiktokCreator?.nickname && (
                <div className="flex items-center gap-2 mb-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)' }}>
                  {tiktokCreator.avatarUrl && (
                    <img src={tiktokCreator.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                  )}
                  <span className="small">Posting as <strong>@{tiktokCreator.nickname}</strong></span>
                </div>
              )}

              {/* Point 1b: Block posting when creator can't post more */}
              {tiktokCreator?.cannotPostReason && (
                <div className="alert alert-danger py-2 mb-2" style={{ fontSize: '12px' }}>
                  <strong>Cannot post right now.</strong> {tiktokCreator.cannotPostReason}. Please try again later.
                </div>
              )}

              {/* Point 5a: Content preview */}
              {(videosAbs.length > 0 || imagesAbs.length > 0) && (
                <div className="mb-2">
                  <label className="form-label small mb-1">Content Preview</label>
                  <div className="flex gap-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)', background: 'var(--bs-tertiary-bg, #f8f9fa)', overflowX: 'auto' }}>
                    {videosAbs.length > 0 ? (
                      <video src={videosAbs[0]} style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 4 }} controls muted />
                    ) : (
                      imagesAbs.slice(0, 4).map((img, i) => (
                        <img key={i} src={img} alt="" style={{ maxHeight: 120, borderRadius: 4, objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* === POINT 2: Post Metadata === */}
              {/* Point 2a: Title */}
              <div className="mb-2">
                <label className="form-label small mb-1">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={tiktokTitle}
                  onChange={(e) => setTiktokTitle(e.target.value)}
                  placeholder="Enter a title for your TikTok post"
                  maxLength={150}
                />
                <div className="form-text">{tiktokTitle.length}/150 characters</div>
              </div>

              {/* Point 2b: Privacy Level - no default, options from creator_info */}
              <div className="mb-2">
                <label className="form-label small mb-1">Privacy Level *</label>
                <select
                  className="form-select"
                  value={tiktokPrivacy}
                  onChange={(e) => {
                    setTiktokPrivacy(e.target.value);
                    if (e.target.value === 'SELF_ONLY' && tiktokBrandContent) {
                      setTiktokBrandContent(false);
                    }
                  }}
                  title={tiktokCommercialToggle && tiktokBrandContent ? 'Branded content visibility cannot be set to private.' : undefined}
                >
                  <option value="" disabled>Select privacy level...</option>
                  {(tiktokCreator?.privacyLevelOptions || ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY']).map(opt => (
                    <option
                      key={opt}
                      value={opt}
                      disabled={opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent}
                      title={opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent ? 'Branded content visibility cannot be set to private.' : undefined}
                    >
                      {opt === 'PUBLIC_TO_EVERYONE' ? 'Public' : opt === 'MUTUAL_FOLLOW_FRIENDS' ? 'Friends Only' : opt === 'FOLLOWER_OF_CREATOR' ? 'Followers' : 'Private (Only Me)'}
                      {opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent ? ' - unavailable for branded content' : ''}
                    </option>
                  ))}
                </select>
                {tiktokCommercialToggle && tiktokBrandContent && (
                  <div className="form-text text-yellow-500">Branded content visibility cannot be set to private.</div>
                )}
              </div>

              {/* Point 2c: Interaction Toggles - unchecked by default, grey out per creator settings */}
              <div className="mb-2">
                <label className="form-label small mb-1">Interaction Settings</label>
                <div className="flex flex-col gap-1">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="tiktok-sp-allow-comment"
                      checked={!tiktokDisableComment}
                      onChange={(e) => setTiktokDisableComment(!e.target.checked)}
                      disabled={tiktokCreator?.commentDisabled}
                    />
                    <label className="form-check-label small" htmlFor="tiktok-sp-allow-comment">
                      Allow Comments{tiktokCreator?.commentDisabled ? ' (disabled in your TikTok settings)' : ''}
                    </label>
                  </div>
                  {videosAbs.length > 0 && (
                    <>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="tiktok-sp-allow-duet"
                          checked={!tiktokDisableDuet}
                          onChange={(e) => setTiktokDisableDuet(!e.target.checked)}
                          disabled={tiktokCreator?.duetDisabled}
                        />
                        <label className="form-check-label small" htmlFor="tiktok-sp-allow-duet">
                          Allow Duet{tiktokCreator?.duetDisabled ? ' (disabled in your TikTok settings)' : ''}
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="tiktok-sp-allow-stitch"
                          checked={!tiktokDisableStitch}
                          onChange={(e) => setTiktokDisableStitch(!e.target.checked)}
                          disabled={tiktokCreator?.stitchDisabled}
                        />
                        <label className="form-check-label small" htmlFor="tiktok-sp-allow-stitch">
                          Allow Stitch{tiktokCreator?.stitchDisabled ? ' (disabled in your TikTok settings)' : ''}
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* === POINT 3: Content Disclosure Setting === */}
              <div className="mb-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)', background: '#3C3C3C', color: '#f1f1f1' }}>
                <div className="font-semibold small mb-1">Content Disclosure Setting</div>
                <div className="form-check form-switch mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="tiktok-sp-commercial"
                    checked={tiktokCommercialToggle}
                    onChange={(e) => {
                      setTiktokCommercialToggle(e.target.checked);
                      if (!e.target.checked) {
                        setTiktokBrandOrganic(false);
                        setTiktokBrandContent(false);
                      }
                    }}
                  />
                  <label className="form-check-label small" htmlFor="tiktok-sp-commercial">
                    This {videosAbs.length > 0 ? 'video' : 'photo'} promotes yourself, a brand, a product, or a service
                  </label>
                </div>
                <div className="form-text">Turn this on if your content is commercial in nature.</div>

                {tiktokCommercialToggle && (
                  <div
                    className="ml-3 mt-2 p-2 rounded"
                    style={{ border: '1px dashed var(--bs-border-color, #dee2e6)' }}
                    title={!tiktokBrandOrganic && !tiktokBrandContent ? 'You need to indicate if your content promotes yourself, a third party, or both.' : undefined}
                  >
                    <div className="small font-semibold mb-1">You must select at least one of the options below:</div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tiktok-sp-brand-organic"
                        checked={tiktokBrandOrganic}
                        onChange={(e) => setTiktokBrandOrganic(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="tiktok-sp-brand-organic">
                        <strong>Your Brand</strong> — I am promoting myself or my own business
                      </label>
                    </div>

                    <div className="form-check mt-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tiktok-sp-brand-content"
                        checked={tiktokBrandContent}
                        onChange={(e) => {
                          setTiktokBrandContent(e.target.checked);
                          if (e.target.checked && tiktokPrivacy === 'SELF_ONLY') {
                            setTiktokPrivacy('PUBLIC_TO_EVERYONE');
                          }
                        }}
                        disabled={tiktokPrivacy === 'SELF_ONLY'}
                        title={tiktokPrivacy === 'SELF_ONLY' ? 'Branded content visibility cannot be set to private.' : undefined}
                      />
                      <label className="form-check-label small" htmlFor="tiktok-sp-brand-content">
                        <strong>Branded Content</strong> — I am promoting another brand or a third party
                        {tiktokPrivacy === 'SELF_ONLY' ? ' (unavailable when privacy is Private)' : ''}
                      </label>
                    </div>

                    {/* Point 3a: Label preview based on selection */}
                    {tiktokBrandOrganic && !tiktokBrandContent && (
                      <div className="alert alert-info py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        Your {videosAbs.length > 0 ? 'video' : 'photo'} will be labeled as &quot;Promotional content&quot;
                      </div>
                    )}
                    {tiktokBrandContent && (
                      <div className="alert alert-info py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        Your {videosAbs.length > 0 ? 'video' : 'photo'} will be labeled as &quot;Paid partnership&quot;
                      </div>
                    )}

                    {/* Validation: at least one must be selected */}
                    {!tiktokBrandOrganic && !tiktokBrandContent && (
                      <div className="alert alert-danger py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        You need to indicate if your content promotes yourself, a third party, or both.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* === POINT 4: Consent declaration === */}
              <div className="alert alert-warning py-2 mb-2" style={{ fontSize: '12px' }}>
                By posting, you agree to TikTok's{' '}
                {tiktokCommercialToggle && (tiktokBrandContent) && (
                  <><a href="https://www.tiktok.com/legal/page/row/bc-policy" target="_blank" rel="noopener noreferrer">Branded Content Policy</a> and{' '}</>
                )}
                <a href="https://www.tiktok.com/legal/page/row/music-usage-confirmation" target="_blank" rel="noopener noreferrer">Music Usage Confirmation</a>.
              </div>

              {/* Point 5d: Processing notification */}
              <div className="alert alert-info py-2 mb-0" style={{ fontSize: '12px' }}>
                After publishing, it may take a few minutes for your content to be processed and visible on your TikTok profile.
              </div>
            </>
          )}
        </div>
      )}

      {/* UPDATED: Schedule Date & Time with TimeSelect + optional daily repeat */}
      <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa", color: "#212529" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <label className="form-label mb-0" style={{ color: "#212529" }}>
            <strong>Schedule Date & Time</strong>
          </label>
          {/* Default is a single post; toggle on to repeat once per day. */}
          <div className="form-check form-switch mb-0">
            <input
              id="schedule-repeat-toggle"
              className="form-check-input"
              type="checkbox"
              checked={repeatMode}
              onChange={(e) => setRepeatMode(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="schedule-repeat-toggle">
              Repeat this post (once per day)
            </label>
          </div>
        </div>

        {!repeatMode ? (
          <>
            {!timezoneReady && (
              <div className="mt-2 text-gray-400 small">Loading timezone…</div>
            )}
            {timezoneReady && (
              <>
                <div className="row mt-2">
                  <div className="col-md-6 mb-2">
                    <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={scheduleDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setScheduleDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Time</label>
                    <TimeSelect
                      value={scheduleTime}
                      onChange={setScheduleTime}
                      tenantTimezone={tenantTimezone}
                      showTimezone={true}
                      id="schedule-time"
                    />
                  </div>
                </div>

                {/* Preview of scheduled time in tenant timezone */}
                {scheduleDate && scheduleTime && (
                  <div className="alert alert-info mt-3 mb-0">
                    📅 Post will be published on{" "}
                    {(() => {
                      try {
                        const utcMySql = localToUTC(scheduleDate, scheduleTime, tenantTimezone);
                        const utcIso = new Date(utcMySql.replace(' ', 'T') + 'Z');
                        return (
                          <strong>
                            {utcIso.toLocaleString('en-US', {
                              timeZone: tenantTimezone,
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </strong>
                        );
                      } catch {
                        return <strong>{scheduleDate} {scheduleTime}</strong>;
                      }
                    })()}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="mt-2">
            <button
              type="button"
              className="btn"
              onClick={() => setCalendarOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                border: '1px solid #ff6800', background: 'transparent', color: '#ff6800',
                borderRadius: 6, padding: '8px 14px', fontSize: 14, cursor: 'pointer',
              }}
            >
              <FaCalendarCheck />
              {repeatSlots.length > 0
                ? `Edit days (${repeatSlots.length} selected)`
                : 'Pick days to repeat'}
            </button>

            {repeatSlots.length > 0 && (
              <div className="alert alert-info mt-3 mb-0">
                📅 This post will be published once per day on{" "}
                <strong>{repeatSlots.length}</strong> day{repeatSlots.length === 1 ? '' : 's'}
                {(() => {
                  const sorted = [...repeatSlots].sort((a, b) => (a.date < b.date ? -1 : 1));
                  const first = sorted[0];
                  const last = sorted[sorted.length - 1];
                  const fmt = (d) => {
                    const [y, m, day] = d.split('-').map(Number);
                    return new Date(Date.UTC(y, m - 1, day, 12)).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric',
                    });
                  };
                  return sorted.length > 1
                    ? <> — from <strong>{fmt(first.date)}</strong> to <strong>{fmt(last.date)}</strong>.</>
                    : <> on <strong>{fmt(first.date)}</strong>.</>;
                })()}
              </div>
            )}

            {/* Duplicate-content ToS warning for strict platforms. */}
            {repeatRiskyPlatforms.length > 0 && (
              <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: 13 }}>
                <strong>Heads up:</strong>{' '}
                {repeatRiskyPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}{' '}
                may flag identical posts repeated every day as spam. Consider varying the caption.
                If a platform rejects the post, we'll automatically pause the remaining days so your
                account stays in good standing.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Images (with text overlay option) */}
      <div className="mb-3">
        <div className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Images:</span>
          {effectiveImages.length > 0 && !generatedVideoUrl && (
            <>
              <label className="small" style={{ marginLeft: 8 }}>
                Sec/image:{' '}
                <input
                  type="number"
                  min={1}
                  max={15}
                  value={imgToVideoSeconds}
                  onChange={(e) => setImgToVideoSeconds(Number(e.target.value) || 4)}
                  style={{ width: 60, padding: '2px 6px' }}
                />
              </label>
              <button
                type="button"
                onClick={handleConvertToVideo}
                disabled={convertingToVideo}
                className="btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', fontSize: 13,
                  border: '1px solid #ff6800', background: 'transparent', color: '#ff6800',
                  borderRadius: 6, cursor: 'pointer',
                }}
                title="Render these images into an MP4 slideshow for video-first platforms"
              >
                {convertingToVideo ? <FaSpinner /> : <FaFilm />}
                {convertingToVideo ? 'Converting…' : 'Convert to Video'}
              </button>
            </>
          )}
          {generatedVideoUrl && (
            <button
              type="button"
              onClick={() => setGeneratedVideoUrl(null)}
              className="btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', fontSize: 13,
                border: '1px solid #6c757d', background: 'transparent', color: '#6c757d',
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              Use images instead
            </button>
          )}
        </div>

        {generatedVideoUrl && (
          <div className="alert alert-info" style={{ marginBottom: 8 }}>
            <FaFilm /> Scheduling the rendered slideshow instead of the still images.
            <video src={generatedVideoUrl} controls style={{ width: '100%', marginTop: 8, borderRadius: 6 }} />
          </div>
        )}

        {effectiveImages.length === 0 ? (
          <div className="text-gray-400">No images provided.</div>
        ) : (
          <>
            {imagesAbs.length > 1 && (
              <div className="form-text mb-2" style={{ color: "#6c757d" }}>
                Drag the <FaGripVertical style={{ verticalAlign: "middle" }} /> handle to reorder. The first image is used as the cover.
              </div>
            )}
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleImageDragEnd}>
              <SortableContext items={imagesAbs} strategy={rectSortingStrategy}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 10,
                  }}
                >
                  {imagesAbs.map((id, imgIdx) => {
                    const isCover = imgIdx === 0;
                    return (
                      <SortableMediaItem
                        key={id}
                        id={id}
                        className="border rounded overflow-hidden"
                        handleVariant={isCover ? "light" : "small"}
                        style={{
                          background: "#111",
                          gridColumn: isCover ? "1 / -1" : undefined,
                          aspectRatio: isCover ? undefined : "4 / 3",
                          marginBottom: isCover ? 4 : 0,
                        }}
                      >
                        <MediaTagOverlay
                          src={effectiveImages[imgIdx]}
                          tags={userTags[imgIdx] || []}
                          disabled
                          onTap={() => setFullSizeImgIdx(imgIdx)}
                          style={isCover ? { width: '100%' } : { width: '100%', height: '100%' }}
                          objectFit={isCover ? "contain" : undefined}
                          onError={(e) => (e.currentTarget.src = "/event.png")}
                        />
                        <button
                          type="button"
                          onClick={() => setEditingImageIdx(imgIdx)}
                          style={{
                            position: "absolute",
                            top: isCover ? 8 : 4,
                            right: isCover ? 8 : 4,
                            background: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            border: "none",
                            borderRadius: isCover ? 6 : 4,
                            padding: isCover ? "6px 12px" : "3px 8px",
                            fontSize: isCover ? 13 : 11,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: isCover ? 5 : 4,
                            zIndex: 3,
                          }}
                          title="Edit image"
                        >
                          <FaEdit />{isCover ? " Edit" : null}
                        </button>
                      </SortableMediaItem>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {platforms.bluesky && blueskyImages.length === 0 && !content.trim() && (
          <div className="form-text mt-2">
            Bluesky supports images (no video). Add at least one image or include text.
          </div>
        )}
      </div>

      {/* Text Overlay Editor */}
      {/* Image Editor */}
      {editingImageIdx !== null && effectiveImages[editingImageIdx] && (
        <MediaEditor
          src={effectiveImages[editingImageIdx]}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          onSaved={handleEditorSaved}
          onClose={() => setEditingImageIdx(null)}
        />
      )}

      {/* Full-size media viewer for tagging */}
      {fullSizeImgIdx !== null && effectiveImages[fullSizeImgIdx] && (
        <FullSizeMediaModal
          src={effectiveImages[fullSizeImgIdx]}
          tags={userTags[fullSizeImgIdx] || []}
          onChange={(tags) => setUserTags(prev => ({ ...prev, [fullSizeImgIdx]: tags }))}
          onClose={() => setFullSizeImgIdx(null)}
        />
      )}

      {/* Videos with subtitle support */}
      {!!videosAbs.length && (
        <div className="mb-3">
          <div className="form-label">Videos:</div>
          {videosAbs.length > 1 && (
            <div className="form-text mb-2" style={{ color: "#6c757d" }}>
              Drag the <FaGripVertical style={{ verticalAlign: "middle" }} /> handle to reorder videos.
            </div>
          )}
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleVideoDragEnd}>
            <SortableContext items={videosAbs} strategy={rectSortingStrategy}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 12,
                  marginTop: 8,
                }}
              >
                {videosAbs.map((u, i) => (
                  <SortableMediaItem key={u} id={u}>
                    <video src={u} controls style={{ width: "100%", borderRadius: 8, display: "block" }}>
                      {videoSubtitles[i] && (
                        <track
                          kind="subtitles"
                          src={videoSubtitles[i]}
                          srcLang="en"
                          label="English"
                          default
                        />
                      )}
                    </video>
                    <button
                      type="button"
                      onClick={() => setEditingVideoIdx(i)}
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        fontSize: 13,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        zIndex: 3,
                      }}
                      title="Edit subtitles"
                    >
                      <FaEdit /> {videoSubtitles[i] ? 'Edit Subtitles' : 'Subtitles'}
                    </button>
                  </SortableMediaItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Subtitle Editor */}
      {editingVideoIdx !== null && videosAbs[editingVideoIdx] && (
        <SubtitleEditor
          src={videosAbs[editingVideoIdx]}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          existingVttUrl={videoSubtitles[editingVideoIdx] || null}
          onSaved={handleSubtitleSaved}
          onClose={() => setEditingVideoIdx(null)}
        />
      )}

      {/* Error message */}
      {!!error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Success message */}
      {!!successMessage && (
        <div className="alert alert-success" role="alert">
          <strong>Success!</strong> {successMessage}
        </div>
      )}

      {/* Music attachment chip */}
      {musicAttachment && (
        <div style={{
          margin: '8px 0', padding: '8px 12px', background: '#1a2240', color: '#fff',
          borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
        }}>
          <FaMusic style={{ color: '#ff6800' }} />
          <span>
            {musicAttachment.tiktokUseNativeSound
              ? 'TikTok native sound (chosen in-app)'
              : `"${musicAttachment.track?.title}" by ${musicAttachment.track?.artist || 'Unknown'}`}
          </span>
          <button type="button" onClick={() => setMusicPickerOpen(true)} style={{
            marginLeft: 'auto', padding: '2px 10px', background: 'transparent',
            color: '#fff', border: '1px solid #5a6a9a', borderRadius: 4, cursor: 'pointer',
          }}>Change</button>
          <button type="button" onClick={removeMusic} style={{
            padding: '2px 10px', background: 'transparent', color: '#ff8e8e',
            border: '1px solid #7a3a3a', borderRadius: 4, cursor: 'pointer',
          }}>Remove</button>
        </div>
      )}

      <MusicPicker
        open={musicPickerOpen}
        onClose={() => setMusicPickerOpen(false)}
        draftRef={musicDraftRef.current}
        sourceVideoUrl={videosAbs[0] || ''}
        selectedPlatforms={Object.keys(platforms).filter(k => platforms[k])}
        currentAttachmentId={musicAttachment?.id}
        onAttached={handleMusicAttached}
      />

      {/* Multi-day repeat picker */}
      <ScheduleCalendar
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        tenantTimezone={tenantTimezone}
        value={repeatSlots}
        onChange={setRepeatSlots}
      />

      {/* Footer - SAME as MediaPost */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
            disabled: busy,
          },
          {
            icon: <FaMusic style={{ color: '#fff' }} />,
            label: musicAttachment ? "Edit music" : "Add music",
            onClick: () => setMusicPickerOpen(true),
          },
          {
            icon: busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />,
            label: busy ? "Scheduling…" : repeatMode ? "Schedule Posts" : "Schedule Post",
            onClick: submit,
            disabled: busy || !canSubmit,
            type: "submit",
          },
        ]}
      />

      <TokenExpiredModal
        isOpen={expiredModal.open}
        platforms={expiredModal.platforms}
        onClose={() => setExpiredModal({ open: false, platforms: [] })}
        onReconnect={() => navigate(`/${tenant}/integrations`)}
      />
    </div>
  );
}